import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomerStatusModalBase } from '../customer-status-modal.base';

/**
 * Confirmation modal used to activate a customer's status; presents an actionable confirmation UI and emits events for confirm or close actions.
 * @component
 * @selector app-customer-status-active-modal
 * @example
 *   <app-customer-status-active-modal [isVisible]="true" [isLoading]="false" (close)="handleClose()" (confirm)="handleConfirm()"></app-customer-status-active-modal>
 * @input {boolean} isVisible - Controls whether the modal is visible. Set to true to show the modal and false to hide it.
 * @input {boolean} isLoading - Indicates a loading/processing state (e.g., disables confirm action and can show a spinner) while the activation is in progress.
 * @output {void} close - Emitted when the user cancels or closes the modal. No payload is sent.
 * @output {void} confirm - Emitted when the user confirms the activation action. No payload is sent.
 *
 * Key behaviors:
 * - Inherits shared modal behavior and UI from CustomerStatusModalBase.
 * - Triggers the close event when the modal is dismissed or cancel is chosen.
 * - Triggers the confirm event when the user confirms activation; isLoading can be used to reflect async operations.
 *
 * Lifecycle and integration notes:
 * - This component is standalone and relies on its template and the base class for core lifecycle handling; it does not add additional lifecycle hooks of its own.
 * - Designed to be used by parent components to show/hide the modal and respond to close/confirm events to perform activation logic and update UI state.
 */
@Component({
  selector: 'app-customer-status-active-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customer-status-active-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-status-active-modal.component.scss'],
})
export class CustomerStatusActiveModalComponent extends CustomerStatusModalBase {
  @Input() isVisible = false;
  @Input() isLoading = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();
}
